"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

import {
  HOMEPAGE_PRIMARY_BUTTON_CLASS,
  HOMEPAGE_SECONDARY_BUTTON_CLASS,
} from "./homepage-button-styles";
import { authLinks, navLinks } from "./navigation-data";
import { TrackedSignupLink } from "./tracked-signup-link";

export function SiteHeader() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    let frameId: number | null = null;

    const onScroll = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        const nextIsScrolled = window.scrollY > 130;
        setIsScrolled((current) =>
          current === nextIsScrolled ? current : nextIsScrolled,
        );
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, []);

  useEffect(() => {
    if (pathname) {
      setMobileOpen(false);
    }
  }, [pathname]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileOpen(false);
      }
    };

    const onResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <header className="fixed inset-x-0 top-3 z-50 px-4 md:top-4 md:px-8">
      <div className="relative mx-auto w-full max-w-container">
        <div
          className={`relative flex min-h-16 items-center justify-between rounded-full border px-4 transition-[background-color,border-color,box-shadow] duration-200 sm:px-6 lg:px-7 ${
            isScrolled
              ? "border-slate-200/90 bg-white/95 shadow-[0_12px_32px_-18px_rgba(15,23,42,0.45)] backdrop-blur-xl"
              : "border-slate-200/80 bg-white/90 shadow-[0_8px_24px_-18px_rgba(15,23,42,0.35)] backdrop-blur-lg"
          }`}
        >
          <div className="flex w-full lg:w-auto items-center justify-between">
            <Link
              href="/"
              prefetch={false}
              className="-ml-2 flex items-center rounded-full px-2 py-1 text-base font-semibold tracking-[-0.01em] text-slate-900 transition hover:text-indigo-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              <Image
                src="/logo.png"
                alt="Feedvote logo"
                width={36}
                height={36}
                className="h-8 w-8 object-contain"
              />
              <span className="ml-2">Feedvote</span>
            </Link>
            <button
              type="button"
              aria-expanded={mobileOpen}
              aria-controls="landing-mobile-menu"
              aria-label="Toggle Menu"
              className="rounded-full border border-slate-200 bg-white p-2.5 text-slate-800 shadow-sm transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 lg:hidden"
              onClick={() => setMobileOpen((value) => !value)}
            >
              {mobileOpen ? (
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  role="img"
                  aria-label="Close menu"
                >
                  <path d="M18.278 16.864a1 1 0 0 1-1.414 1.414l-4.829-4.828-4.828 4.828a1 1 0 0 1-1.414-1.414l4.828-4.829-4.828-4.828a1 1 0 0 1 1.414-1.414l4.829 4.828 4.828-4.828a1 1 0 1 1 1.414 1.414l-4.828 4.829z" />
                </svg>
              ) : (
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  role="img"
                  aria-label="Open menu"
                >
                  <path d="M4 5h16a1 1 0 1 1 0 2H4a1 1 0 1 1 0-2m0 6h16a1 1 0 1 1 0 2H4a1 1 0 1 1 0-2m0 6h16a1 1 0 1 1 0 2H4a1 1 0 1 1 0-2" />
                </svg>
              )}
            </button>
          </div>

          <nav
            id="landing-mobile-menu"
            className={`${
              mobileOpen ? "flex" : "hidden"
            } absolute left-0 right-0 top-[calc(100%+0.75rem)] rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_22px_50px_-24px_rgba(15,23,42,0.35)] flex-col gap-4 lg:static lg:flex lg:w-auto lg:flex-row lg:items-center lg:gap-2 lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none`}
          >
            <ul className="flex w-full flex-col gap-1 lg:w-auto lg:flex-row lg:gap-3">
              {navLinks.map((link) => {
                const isExternalLink = link.href.startsWith("http");

                return (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      prefetch={false}
                      target={isExternalLink ? "_blank" : undefined}
                      rel={isExternalLink ? "noopener noreferrer" : undefined}
                      className="flex rounded-full px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 hover:text-indigo-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                      onClick={() => setMobileOpen(false)}
                    >
                      {link.label}
                    </Link>
                  </li>
                );
              })}
            </ul>

            <div className="mt-1 flex flex-col items-stretch gap-3 sm:flex-row lg:hidden">
              <Link
                href={authLinks[0].href}
                prefetch={false}
                className={`${HOMEPAGE_SECONDARY_BUTTON_CLASS} w-full`}
                onClick={() => setMobileOpen(false)}
              >
                {authLinks[0].label}
              </Link>
              <TrackedSignupLink
                ctaLabel={authLinks[1].label}
                placement="mobile_header"
                className={`${HOMEPAGE_PRIMARY_BUTTON_CLASS} w-full`}
                onClick={() => setMobileOpen(false)}
              >
                {authLinks[1].label}
              </TrackedSignupLink>
            </div>
          </nav>

          <div className="ml-4 hidden items-center gap-2 lg:flex">
            <Link
              href={authLinks[0].href}
              prefetch={false}
              className="rounded-full px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 hover:text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              {authLinks[0].label}
            </Link>
            <TrackedSignupLink
              ctaLabel={authLinks[1].label}
              placement="desktop_header"
              className={HOMEPAGE_PRIMARY_BUTTON_CLASS}
            >
              <span>{authLinks[1].label}</span>
              <svg
                className="w-4 h-4 transition-transform group-hover:translate-x-1"
                viewBox="0 0 20 20"
                fill="currentColor"
                role="img"
                aria-label="Arrow right"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02"
                />
              </svg>
            </TrackedSignupLink>
          </div>
        </div>
      </div>
    </header>
  );
}
